import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import TestEnvBanner from "../components/TestEnvBanner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  User,
  Mail,
  FileText,
  Receipt,
  Banknote,
  Building2,
  CalendarIcon,
  Loader2,
  FlaskConical,
  Upload,
  AlertCircle,
  Folder,
  FolderOpen,
  ChevronRight as ChevronRightIcon,
  ChevronDown,
  X
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { InfoTooltip } from "../components/InfoTooltip";
import ContactSearch from "../components/ContactSearch";
import MoneyBar from "../components/MoneyBar";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import useMatterPrefill from "../hooks/useMatterPrefill";
import SharePointMatterFeeEarner from "../components/SharePointMatterFeeEarner";
import FixedFeeNarrativePicker from "../components/FixedFeeNarrativePicker";
import DisbursementsOnlyPicker from "../components/DisbursementsOnlyPicker";

import { LOGO_URL } from "../config/constants";
import { INVOICE_REQUEST_FEES, FEE_COUNT_OPTIONS, feeRate } from "../lib/invoiceRequestFees";
import { errorMessage } from "../utils/errorMessage";

export const STEPS = [
  { id: 1, title: "Invoice Info", icon: User },
  { id: 2, title: "Client Contact", icon: Mail },
  { id: 3, title: "Disbursements", icon: Receipt },
  { id: 4, title: "Invoice Type", icon: FileText },
  { id: 5, title: "MOA & Third Party", icon: Banknote },
  { id: 6, title: "Delivery", icon: Building2 },
  { id: 7, title: "Review", icon: Check }
];


export default function InvoiceRequest() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thirdPartyFile, setThirdPartyFile] = useState(null);
  const [showSpPicker, setShowSpPicker] = useState(false);
  
  const [formData, setFormData] = useState({
    form_type: "invoice_request",
    // Step 1: Invoice Information
    requester_name: "",
    requester_email: "",
    date: format(new Date(), "yyyy-MM-dd"),
    client_name: "",
    client_email: "",
    matter_title: "",
    matter_reference: "",
    // Set when the picked matter came from SharePoint (pre-CM, no CM record).
    // Drives the fee-earner prompt below the matter search.
    matter_source: "",
    responsible_fee_earner: "",
    // Step 2: Client Contact
    client_invoice_email: "",
    invoice_comments: "",
    // Step 3: Disbursements
    disbursements_noted: "",
    accounts_add_disbursements: "",
    ...Object.fromEntries(INVOICE_REQUEST_FEES.map(f => [f.field, "0"])),
    other_disbursement: "",
    lawbillity_expenses: [],
    // Step 4: Invoice Type
    invoice_type: "",
    wip_or_fixed: "",
    wip_time_checked: "",
    // Brief 33 — fixed-fee amount + narrative capture. Both mandatory
    // when wip_or_fixed === "Fixed fee"; hidden + ignored otherwise.
    // Amount is a free-text string (Lawbillity owns calculation).
    // Narrative IS the client-facing invoice line — 20-500 chars.
    fixed_fee_amount: "",
    fixed_fee_narrative: "",
    // BM-H347 — which standard narrative the fee earner picked, if any.
    // Stored alongside the narrative so template usage (and how often a
    // template gets rewritten) is measurable later. Free text after the
    // pick: the narrative field stays editable and this code stays put.
    fixed_fee_narrative_template_code: "",
    // The picked matter's transaction type, when the matter is known to
    // CaseMinder. Used only to filter the narrative picker.
    matter_transaction_type: "",
    // Step 5: MOA & Third Party
    hold_moa: "",
    use_moa: "",
    moa_retention_checked: "",
    third_party_invoice: "",
    third_party_file_name: "",
    third_party_sp_url: "",
    cto_notification: "",
    // Step 6: Delivery
    draft_required: "",
    notification_preference: "",
    further_info: "",
    // 27 Oct 2026 (May Ann): raised from a completion statement
    // (?cs=ID); the draft invoice is built from the statement's lines.
    completion_statement_id: "",
    // "Disbursements only": logged disbursements unticked on step 3.
    excluded_disbursement_ids: [],
    // Test flag
    is_test_submission: false
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // BM-H3723: opened from a matter page (?matter=REF), start with that
  // matter filled in.
  // Vanessa, 27 Oct 2026: the request opens "largely prepopulated", so
  // "who is submitting" is the signed-in user unless already typed.
  useEffect(() => {
    let u = null;
    try { u = JSON.parse(localStorage.getItem("user") || "null"); } catch { u = null; }
    if (!u) return;
    setFormData(prev => ({
      ...prev,
      requester_name: prev.requester_name || u.name || "",
      requester_email: prev.requester_email || u.email || "",
    }));
  }, []);

  const matterPrefillKey = useMatterPrefill(setFormData, ["client_name", "client_email", "matter_title", "responsible_fee_earner"]);

  // 27 Oct 2026 (May Ann). ?cs=ID: opened from a completion statement's
  // "Raise invoice request", so fill the fee from the statement.
  // ?basis=disbursements: Finance > Invoices "Disbursements only", the
  // short request (no Invoice Type step).
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const statementId = params.get("cs") || "";
  const shortDisbursements = params.get("basis") === "disbursements";
  const [statement, setStatement] = useState(null);

  useEffect(() => {
    if (!shortDisbursements) return;
    setFormData(prev => ({
      ...prev,
      wip_or_fixed: "Disbursements only",
      invoice_type: prev.invoice_type || "Interim",
      disbursements_noted: prev.disbursements_noted || "Yes",
      accounts_add_disbursements: prev.accounts_add_disbursements || "No",
    }));
  }, [shortDisbursements]);

  useEffect(() => {
    if (!statementId) return undefined;
    let cancelled = false;
    axios.get(`${API}/completion-statement/${encodeURIComponent(statementId)}/invoice-prefill`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data.existing_request_id) {
          toast.info(`An invoice request${data.existing_request_number ? ` (${data.existing_request_number})` : ""} was already raised from this statement. Opening it.`);
          navigate(`/submission/${data.existing_request_id}`, { replace: true });
          return;
        }
        setStatement(data);
        setFormData(prev => ({
          ...prev,
          completion_statement_id: data.statement_id,
          matter_reference: prev.matter_reference || data.matter_reference,
          client_name: prev.client_name || data.client_name,
          wip_or_fixed: prev.wip_or_fixed || "Fixed fee",
          fixed_fee_amount: prev.fixed_fee_amount || data.fixed_fee_amount,
          fixed_fee_narrative: prev.fixed_fee_narrative || data.fixed_fee_narrative,
        }));
      })
      .catch(err => {
        if (!cancelled) toast.error(errorMessage(err, "Could not read the completion statement"));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementId]);

  const validateStep = (step) => {
    const errors = [];
    
    switch (step) {
      case 1:
        if (!formData.requester_name) errors.push("Requester name is required");
        if (!formData.requester_email) errors.push("Email is required");
        if (!formData.date) errors.push("Date is required");
        if (!formData.client_name) errors.push("Client name is required");
        if (!formData.matter_title) errors.push("Matter title is required");
        if (!formData.matter_reference) errors.push("Matter reference is required");
        if (formData.matter_source === "sharepoint" && !formData.responsible_fee_earner)
          errors.push("Please choose a fee earner for this SharePoint matter");
        break;
      case 2:
        if (!formData.client_invoice_email) errors.push("Client invoice email is required");
        break;
      case 3:
        if (!formData.disbursements_noted) errors.push("Please confirm disbursements status");
        if (!formData.accounts_add_disbursements) errors.push("Please indicate if Accounts should add disbursements");
        break;
      case 4:
        if (!formData.invoice_type) errors.push("Invoice type is required");
        if (!formData.wip_or_fixed) errors.push("WIP or Fixed fee selection is required");
        if (formData.wip_or_fixed === "WIP" && !formData.wip_time_checked) {
          errors.push("Please confirm time recordings have been checked");
        }
        // Brief 33 — fixed-fee validation mirrors the WIP -> wip_time_checked
        // conditional. Fires only when the fee earner has picked "Fixed fee".
        // Amount is free-text (Lawbillity calculates); narrative is what
        // hits the client invoice so it must be substantive (>=20 chars).
        if (formData.wip_or_fixed === "Fixed fee") {
          const ffAmt = (formData.fixed_fee_amount || "").trim();
          const ffNarr = (formData.fixed_fee_narrative || "").trim();
          if (!ffAmt) {
            errors.push("Fixed fee amount is required");
          } else if (ffAmt.length > 100) {
            errors.push("Fixed fee amount must be at most 100 characters");
          }
          if (!ffNarr) {
            errors.push("Fixed fee narrative is required");
          } else if (ffNarr.length < 20) {
            errors.push("Fixed fee narrative must be at least 20 characters");
          } else if (ffNarr.length > 500) {
            errors.push("Fixed fee narrative must be at most 500 characters");
          }
        }
        break;
      case 5:
        if (!formData.hold_moa) errors.push("Please indicate MOA status");
        if (formData.hold_moa === "Yes" && !formData.use_moa) {
          errors.push("Please indicate if MOA should be used");
        }
        if (formData.use_moa === "Yes" && !formData.moa_retention_checked) {
          errors.push("Please confirm retention check");
        }
        if (!formData.third_party_invoice) errors.push("Please indicate third party invoice status");
        if (formData.third_party_invoice === "Yes") {
          if (!thirdPartyFile && !formData.third_party_sp_url) errors.push("Please upload the third party invoice or pick from SharePoint");
          if (!formData.cto_notification) errors.push("Please indicate CTO notification preference");
        }
        break;
      case 6:
        if (!formData.draft_required) errors.push("Please indicate if draft is required");
        if (!formData.notification_preference) errors.push("Please select notification preference");
        break;
      default:
        break;
    }
    
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (errors.length > 0) {
      errors.forEach(e => toast.error(e));
      return;
    }
    setCurrentStep(prev => {
      const next = Math.min(prev + 1, STEPS.length);
      return shortDisbursements && next === 4 ? 5 : next;
    });
  };

  const handleBack = () => {
    setCurrentStep(prev => {
      const back = Math.max(prev - 1, 1);
      return shortDisbursements && back === 4 ? 3 : back;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThirdPartyFile(file);
      updateField("third_party_file_name", file.name);
      updateField("third_party_sp_url", "");
    }
  };

  const handleSharePointFile = (file) => {
    updateField("third_party_file_name", file.name);
    updateField("third_party_sp_url", file.webUrl || "");
    setThirdPartyFile(null); // Clear device file — using SP reference
    setShowSpPicker(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // First create the submission
      const response = await axios.post(`${API}/submissions`, {
        data: formData
      });
      
      const submissionId = response.data.id;
      
      // If there's a third-party invoice file, upload it to SharePoint
      if (thirdPartyFile && formData.third_party_invoice === "Yes") {
        try {
          const fileFormData = new FormData();
          fileFormData.append('file', thirdPartyFile);
          fileFormData.append('file_type', 'third_party_invoice');
          if (formData.matter_reference) {
            fileFormData.append('matter_reference', formData.matter_reference);
          }

          const uploadRes = await axios.post(`${API}/submissions/${submissionId}/upload-file`, fileFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });

          if (uploadRes.data?.sharepoint_url) {
            updateField("third_party_sp_url", uploadRes.data.sharepoint_url);
          }

          toast.success("File uploaded to SharePoint \u2713");
        } catch (fileError) {
          console.error("File upload error:", fileError);
          toast.warning("Invoice request submitted, but file upload failed. Please re-upload from the detail page.");
        }
      } else {
        toast.success("Invoice request submitted successfully!");
      }

      // BM-H641: a final invoice is the moment to close the file.
      if (formData.invoice_type === "Final" && formData.matter_reference) {
        const q = new URLSearchParams({
          matter_reference: formData.matter_reference,
          client_name: formData.client_name || "",
        });
        toast.info("Final invoice requested. Close the file when it's paid.", {
          duration: 15000,
          action: { label: "Close the file", onClick: () => navigate(`/file-closure?${q.toString()}`) },
        });
      }

      navigate(`/submission/${submissionId}`);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit invoice request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          
          return (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "w-10 h-10 rounded-md flex items-center justify-center border-2 transition-all",
                    isCompleted && "bg-teal border-teal text-white",
                    isCurrent && "bg-white border-teal text-teal",
                    !isCompleted && !isCurrent && "bg-gray-100 border-gray-300 text-gray-400"
                  )}
                >
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={cn(
                  "mt-2 text-xs font-medium hidden sm:block",
                  isCurrent ? "text-teal" : "text-gray-500"
                )}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={cn(
                  "w-12 md:w-20 h-0.5 mx-2",
                  currentStep > step.id ? "bg-teal" : "bg-gray-200"
                )} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <TestEnvBanner />
      {/* Header */}
      <header className="glass-nav sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Bonsai Law" className="h-10 w-auto" />
          </Link>
          <h1 className="text-base font-semibold text-white">Invoice Request</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8 animate-fade-in-up">
        {renderProgressBar()}

        <div className="glass-card rounded-md p-6 md:p-8">
          {statement && <StatementBanner statement={statement} />}
          {shortDisbursements && (
            <div className="mb-6 p-3 rounded-md bg-indigo-50 border border-indigo-200 text-sm text-slate-700" data-testid="disb-only-banner">
              Disbursements only. Untick anything that shouldn't be billed; there is no fee to fill in.
            </div>
          )}
          {/* Step Content */}
          {currentStep === 1 && <Step1InvoiceInfo formData={formData} matterPrefillKey={matterPrefillKey} updateField={updateField} updateFields={(fields) => setFormData(prev => ({ ...prev, ...fields }))} />}
          {currentStep === 2 && <Step2ClientContact formData={formData} updateField={updateField} />}
          {currentStep === 3 && <Step3Disbursements formData={formData} updateField={updateField} currentStep={currentStep} />}
          {currentStep === 4 && <Step4InvoiceType formData={formData} updateField={updateField} />}
          {currentStep === 5 && <Step5MOAThirdParty formData={formData} updateField={updateField} thirdPartyFile={thirdPartyFile} onFileChange={handleFileChange} onSharePointFile={() => setShowSpPicker(true)} />}
          {currentStep === 6 && <Step6Delivery formData={formData} updateField={updateField} />}
          {currentStep === 7 && <Step7Review formData={formData} thirdPartyFile={thirdPartyFile} />}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? () => navigate("/") : handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {currentStep === 1 ? "Cancel" : "Back"}
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                className="bg-teal hover:bg-teal-hover text-white flex items-center gap-2"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-gold hover:bg-gold text-white flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Submit Invoice Request
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* SharePoint File Picker Modal */}
      {showSpPicker && (
        <SharePointFilePicker
          matterReference={formData.matter_reference}
          onSelect={handleSharePointFile}
          onClose={() => setShowSpPicker(false)}
        />
      )}
    </div>
  );
}

// SharePoint File Picker Modal
function SharePointFilePicker({ matterReference, onSelect, onClose }) {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driveId, setDriveId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [error, setError] = useState(null);

  const getHeaders = () => {
    const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
    return { "X-Bonsai-User-Id": uid };
  };

  const loadFolder = async (path, itemId, drv, crumb) => {
    setLoading(true);
    setError(null);
    try {
      const params = itemId
        ? { drive_id: drv || driveId, item_id: itemId, include_files: true }
        : { path: path || "SL", include_files: true };
      const res = await axios.get(`${API}/bonsai/mail/sp-browse`, { params, headers: getHeaders() });
      setFolders(res.data.folders || []);
      setFiles(res.data.files || []);
      if (res.data.driveId) setDriveId(res.data.driveId);
      setBreadcrumb(crumb || [{ name: "SL", path: "SL" }]);
    } catch {
      setError("Failed to load SharePoint folders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (matterReference) {
      // Start directly at the matter folder
      setLoading(true);
      setError(null);
      axios.get(`${API}/bonsai/mail/sp-browse`, {
        params: { matter_ref: matterReference, include_files: true },
        headers: getHeaders(),
      }).then(res => {
        setFolders(res.data.folders || []);
        setFiles(res.data.files || []);
        if (res.data.driveId) setDriveId(res.data.driveId);
        setBreadcrumb([{ name: matterReference, id: res.data.parentItemId }]);
      }).catch(() => {
        // Fall back to root
        loadFolder("SL");
      }).finally(() => setLoading(false));
    } else {
      loadFolder("SL");
    }
  }, []);

  const openFolder = (folder) => {
    const newCrumb = [...breadcrumb, { name: folder.name, id: folder.id }];
    loadFolder(null, folder.id, driveId, newCrumb);
  };

  const goToCrumb = (index) => {
    const crumb = breadcrumb.slice(0, index + 1);
    const item = crumb[crumb.length - 1];
    if (item.path) loadFolder(item.path, null, null, crumb);
    else loadFolder(null, item.id, driveId, crumb);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-gray-900">Pick file from SharePoint</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Breadcrumb */}
        <div className="px-4 py-2 border-b bg-gray-50 flex items-center gap-1 text-xs text-gray-500 flex-wrap">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRightIcon className="w-3 h-3" />}
              <button onClick={() => goToCrumb(i)} className="hover:text-blue-600 hover:underline">{b.name}</button>
            </span>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
          {error && <p className="text-status-red text-sm text-center py-4">{error}</p>}
          {!loading && !error && folders.length === 0 && files.length === 0 && (
            <p className="text-gray-400 text-sm text-center py-4">Empty folder</p>
          )}
          {!loading && folders.map(f => (
            <button key={f.id} onClick={() => openFolder(f)}
              className="flex items-center gap-2 w-full px-3 py-2 rounded hover:bg-gray-100 text-left text-sm">
              <Folder className="w-4 h-4 text-status-amber" />
              <span className="truncate">{f.name}</span>
              <ChevronRightIcon className="w-3 h-3 text-gray-300 ml-auto" />
            </button>
          ))}
          {!loading && files.map(f => {
            const sizeKB = f.size ? Math.round(f.size / 1024) : 0;
            const sizeLabel = sizeKB > 1024 ? (sizeKB / 1024).toFixed(1) + " MB" : sizeKB + " KB";
            return (
              <button key={f.id} onClick={() => onSelect(f)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded hover:bg-blue-50 text-left text-sm group">
                <FileText className="w-4 h-4 text-blue-500" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-xs text-gray-400">{sizeLabel}</span>
                <Check className="w-4 h-4 text-blue-500 opacity-0 group-hover:opacity-100" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Step 1: Invoice Information
// BM-H3862: matterPrefillKey comes in as a prop; it lives in the page component.
function Step1InvoiceInfo({ formData, matterPrefillKey, updateField, updateFields }) {
  const handleContactSelect = (data) => {
    const fields = {};
    if (data.client_name !== undefined) fields.client_name = data.client_name;
    if (data.client_email !== undefined) fields.client_email = data.client_email;
    if (data.matter_reference !== undefined) fields.matter_reference = data.matter_reference;
    if (data.matter_title !== undefined) fields.matter_title = data.matter_title;
    // Brief 6 (28 May 2026): pre-fill the Step-2 "Client email address to
    // be used for invoice" from the Step-1 client selection, but ONLY when
    // it's currently blank. Preserves user override on going back-and-forth.
    if (data.client_email && !formData.client_invoice_email) {
      fields.client_invoice_email = data.client_email;
    }
    updateFields(fields);
  };

  const handleContactClear = () => {
    // Brief 6 (28 May 2026): clear the auto-filled invoice email too, but
    // ONLY if it still equals the previous client_email (i.e. user never
    // manually edited it). Preserves user override.
    const cleared = { client_name: "", client_email: "" };
    if (formData.client_invoice_email && formData.client_invoice_email === formData.client_email) {
      cleared.client_invoice_email = "";
    }
    updateFields(cleared);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Invoice Information</h2>
        <p className="text-gray-600 text-sm">Basic details about this invoice request</p>
      </div>

      <div className="grid gap-4">
        <div>
          <Label htmlFor="requester_name" className="text-sm font-medium">
            Who is submitting this invoice request? <span className="text-status-red">*</span>
          </Label>
          <Input
            id="requester_name"
            value={formData.requester_name}
            onChange={(e) => updateField("requester_name", e.target.value)}
            placeholder="Enter your name"
            className="mt-1"
            data-testid="requester-name-input"
          />
        </div>

        <div>
          <Label htmlFor="requester_email" className="text-sm font-medium">
            Your email address <span className="text-status-red">*</span>
          </Label>
          <Input
            id="requester_email"
            type="email"
            value={formData.requester_email}
            onChange={(e) => updateField("requester_email", e.target.value)}
            placeholder="you@bonsai.law"
            className="mt-1"
            data-testid="requester-email-input"
          />
        </div>

        <div>
          <Label htmlFor="date" className="text-sm font-medium">
            Date <span className="text-status-red">*</span>
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal mt-1",
                  !formData.date && "text-muted-foreground"
                )}
                data-testid="date-picker"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date ? format(new Date(formData.date), "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.date ? new Date(formData.date) : undefined}
                onSelect={(date) => updateField("date", date ? format(date, "yyyy-MM-dd") : "")}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <ContactSearch
          label="Client name"
          selectedName={formData.client_name}
          selectedEmail={formData.client_email}
          onSelect={handleContactSelect}
          onClear={handleContactClear}
          onTypedName={(name) => updateField("client_name", name)}
        />

        <div>
          <Label htmlFor="matter_title" className="text-sm font-medium">
            Matter title <span className="text-status-red">*</span>
          </Label>
          <Input
            id="matter_title"
            value={formData.matter_title}
            onChange={(e) => updateField("matter_title", e.target.value)}
            placeholder="Enter the matter title"
            className="mt-1"
            data-testid="matter-title-input"
          />
        </div>

        <div>
          <Label htmlFor="matter_reference" className="text-sm font-medium">
            Matter reference <span className="text-status-red">*</span>
          </Label>
          {/* Brief #4 — SmartClientMatterSearch rollout (Block 3 table
              row #4: feeEarnerScoped=true; the component does not expose
              a scoping prop, intent captured here as code intent). */}
          <SmartClientMatterSearch
            key={matterPrefillKey}
            label={null}
            placeholder="e.g., VC/12345/001"
            initialValue={formData.matter_reference}
            onSelect={(result) => {
              const ref = result.matter_reference || result.label || "";
              updateField("matter_reference", ref);
              if (!formData.client_name && result.client_name) {
                updateField("client_name", result.client_name);
              }
              if (!formData.matter_title && result.matter_title) {
                updateField("matter_title", result.matter_title);
              }
              // SharePoint-only matters carry no fee earner — flag the source
              // so the prompt below appears. CM matters clear it (and keep any
              // fee earner they already carry).
              updateField("matter_source", result.source || "");
              // BM-H347 — carry the matter's transaction type so the
              // fixed-fee narrative picker can filter to it. Absent on
              // typed/SharePoint-only picks, which is handled by the
              // picker showing the whole library.
              updateField("matter_transaction_type", result.matter_type || "");
              if (result.source !== "sharepoint" && result.fee_earner) {
                updateField("responsible_fee_earner", result.fee_earner);
              }
            }}
            className="mt-1"
          />
          <SharePointMatterFeeEarner
            source={formData.matter_source}
            value={formData.responsible_fee_earner}
            onChange={(name) => updateField("responsible_fee_earner", name)}
          />
        </div>
      </div>

      {/* Test Submission Flag */}
      <div className="p-4 bg-status-amber-dim/30 border border-amber-500/40 rounded-md mt-6">
        <div className="flex items-start space-x-3">
          <Checkbox
            id="is_test_submission"
            checked={formData.is_test_submission}
            onCheckedChange={(checked) => updateField("is_test_submission", checked)}
            className="mt-0.5 border-amber-400 data-[state=checked]:bg-status-amber-dim/300"
            data-testid="test-submission-checkbox"
          />
          <div>
            <Label htmlFor="is_test_submission" className="font-medium text-status-amber cursor-pointer flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              This is a test submission (for internal pilot only)
            </Label>
            <p className="text-xs text-status-amber mt-1">
              Test submissions will be marked with [TEST] and can be filtered/deleted later.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Step 2: Client Contact
function Step2ClientContact({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Client Contact</h2>
        <p className="text-gray-600 text-sm">Where should the invoice be sent?</p>
      </div>

      <div className="grid gap-4">
        <div>
          <Label htmlFor="client_invoice_email" className="text-sm font-medium">
            Client email address to be used for invoice <span className="text-status-red">*</span>
          </Label>
          <Input
            id="client_invoice_email"
            type="email"
            value={formData.client_invoice_email}
            onChange={(e) => updateField("client_invoice_email", e.target.value)}
            placeholder="client@example.com"
            className="mt-1"
            data-testid="client-invoice-email-input"
          />
        </div>

        <div>
          <Label htmlFor="invoice_comments" className="text-sm font-medium">
            Any comments about approach to clients when sending the invoice or comments to be included?
          </Label>
          <Textarea
            id="invoice_comments"
            value={formData.invoice_comments}
            onChange={(e) => updateField("invoice_comments", e.target.value)}
            placeholder="For example, you may wish to draw attention to a discount"
            className="mt-1 min-h-[100px]"
            data-testid="invoice-comments-input"
          />
          <p className="text-xs text-gray-500 mt-1">Optional</p>
        </div>
      </div>
    </div>
  );
}

// 27 Oct 2026: what came across from the completion statement.
export function StatementBanner({ statement }) {
  const gbp = (v) => `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const unchecked = statement.statement_status === "submitted";
  return (
    <div className="mb-6 p-4 rounded-md bg-indigo-50 border border-indigo-200 text-sm text-slate-700 space-y-2" data-testid="cs-invoice-banner">
      <p className="font-medium">Filled in from the completion statement. Check it and carry on.</p>
      {unchecked && (
        <p className="text-status-amber" data-testid="cs-invoice-unchecked">This statement hasn't had its independent check yet.</p>
      )}
      <ul className="space-y-0.5">
        {statement.legal_fees.map((f, i) => (
          <li key={`f${i}`} className="flex justify-between gap-3"><span>{f.description || "Legal fees"}</span><span className="tabular-nums">{gbp(f.net)} + VAT</span></li>
        ))}
        {statement.disbursements.map((d, i) => (
          <li key={`d${i}`} className="flex justify-between gap-3 text-slate-500"><span>{d.description}</span><span className="tabular-nums">{gbp(d.net)}{Number(d.vat) > 0 ? " + VAT" : ""}</span></li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">Accounts' draft invoice uses these lines.</p>
    </div>
  );
}

// Step 3: Disbursements
function Step3Disbursements({ formData, updateField, currentStep }) {
  const [lbExpenses, setLbExpenses] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbError, setLbError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [hasFetched, setHasFetched] = useState(false);
  const [financialSummary, setFinancialSummary] = useState(null);

  // Fetch financial summary when step 3 is reached
  useEffect(() => {
    if (currentStep !== 3 || !formData.matter_reference) return;
    let cancelled = false;
    axios
      .get(`${API}/money-on-account/${encodeURIComponent(formData.matter_reference)}/financial-summary`)
      .then((res) => { if (!cancelled) setFinancialSummary(res.data); })
      .catch((err) => {
        // BM-H153 (finance audit P1-8): the invoice request wizard
        // relies on this financial-summary to show the fee earner
        // WIP / MoA balance before they invoice, so a silent .catch
        // meant they were invoicing blind when the endpoint failed.
        // Surface the error and leave financialSummary null so the
        // UI still renders without the badge.
        if (cancelled) return;
        console.error("Matter financial summary failed:", err);
        if (window.errorMessage) {
          window.errorMessage(
            `Matter financial summary failed to load: ${err?.response?.data?.detail || err?.message || "unknown error"}`
          );
        }
      });
    return () => { cancelled = true; };
  }, [currentStep, formData.matter_reference]);

  // Fetch LawBillity expenses when step 3 is reached
  useEffect(() => {
    if (currentStep !== 3 || hasFetched || !formData.matter_reference) return;

    let cancelled = false;
    setLbLoading(true);
    setLbError(null);

    axios
      .get(`${API}/lawbillity/expenses/${encodeURIComponent(formData.matter_reference)}`)
      .then((res) => {
        if (cancelled) return;
        const expenses = res.data?.expenses || [];
        setLbExpenses(expenses);
        // Select all by default
        setSelectedIds(new Set(expenses.map((_, i) => i)));
        // Store in formData
        updateField("lawbillity_expenses", expenses);
      })
      .catch(() => {
        if (cancelled) return;
        setLbError("Could not fetch disbursements from LawBillity");
      })
      .finally(() => {
        if (!cancelled) {
          setLbLoading(false);
          setHasFetched(true);
        }
      });

    return () => { cancelled = true; };
  }, [currentStep, formData.matter_reference]);

  // Sync selected expenses to formData whenever selection changes
  useEffect(() => {
    if (lbExpenses.length === 0) return;
    const selected = lbExpenses.filter((_, i) => selectedIds.has(i));
    updateField("lawbillity_expenses", selected);
  }, [selectedIds]);

  const toggleExpense = (idx) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === lbExpenses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(lbExpenses.map((_, i) => i)));
    }
  };

  const selectedTotal = lbExpenses
    .filter((_, i) => selectedIds.has(i))
    .reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Disbursements</h2>
        <p className="text-gray-600 text-sm">Confirm disbursement details for this invoice</p>
      </div>

      {/* Financial Position Card */}
      {financialSummary && (financialSummary.quote > 0 || financialSummary.wip > 0 || financialSummary.money_on_account > 0) && (
        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="financial-position-card">
          <h3 className="font-medium text-slate-700 text-sm mb-3 flex items-center gap-2">
            <Banknote className="w-4 h-4 text-teal" />
            Financial Position
          </h3>
          <MoneyBar
            quote={financialSummary.quote}
            wip={financialSummary.wip}
            moneyOnAccount={financialSummary.money_on_account}
          />
        </div>
      )}

      {/* LawBillity Expenses Section */}
      <div className="space-y-3">
        <h3 className="font-medium text-gray-900 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-teal" />
          LawBillity Expenses
        </h3>

        {lbLoading && (
          <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-md border border-gray-200">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            <span className="text-sm text-gray-500">Fetching expenses from LawBillity...</span>
          </div>
        )}

        {lbError && (
          <div className="p-3 bg-status-red-dim border border-red-200 rounded-md text-sm text-status-red flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {lbError}
          </div>
        )}

        {!lbLoading && !lbError && hasFetched && lbExpenses.length === 0 && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500">
            No disbursements found in LawBillity{formData.matter_reference ? ` for ${formData.matter_reference}` : ""}. Use the manual fields below if needed.
          </div>
        )}

        {!lbLoading && lbExpenses.length > 0 && (
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left w-10">
                    <Checkbox
                      checked={selectedIds.size === lbExpenses.length}
                      onCheckedChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-gray-600 font-medium">Description</th>
                  <th className="px-3 py-2 text-right text-gray-600 font-medium">Amount</th>
                  <th className="px-3 py-2 text-left text-gray-600 font-medium">Date</th>
                  <th className="px-3 py-2 text-left text-gray-600 font-medium">Category</th>
                  <th className="px-3 py-2 text-center text-gray-600 font-medium">Taxable</th>
                </tr>
              </thead>
              <tbody>
                {lbExpenses.map((exp, idx) => (
                  <tr
                    key={idx}
                    className={cn(
                      "border-b border-gray-100 hover:bg-gray-50 cursor-pointer",
                      !selectedIds.has(idx) && "opacity-50"
                    )}
                    onClick={() => toggleExpense(idx)}
                  >
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={selectedIds.has(idx)}
                        onCheckedChange={() => toggleExpense(idx)}
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">{exp.description || "—"}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">
                      £{(exp.amount || 0).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{exp.date || "—"}</td>
                    <td className="px-3 py-2 text-gray-600">{exp.category || "—"}</td>
                    <td className="px-3 py-2 text-center">
                      {exp.is_taxable ? (
                        <span className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-status-amber-dim text-status-amber rounded">VAT</span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {selectedIds.size} of {lbExpenses.length} selected
              </span>
              <span className="text-sm font-semibold text-gray-900">
                Total: £{selectedTotal.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {!formData.matter_reference && !lbLoading && !hasFetched && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500">
            No matter reference provided — LawBillity expense lookup skipped.
          </div>
        )}
      </div>

      {formData.wip_or_fixed === "Disbursements only" && formData.matter_reference && (
        <DisbursementsOnlyPicker
          matterRef={formData.matter_reference}
          excluded={formData.excluded_disbursement_ids}
          onChange={(ids) => updateField("excluded_disbursement_ids", ids)}
        />
      )}

      {/* Existing manual disbursement fields */}
      <div className="space-y-6 pt-4 border-t border-gray-200">
        <div>
          <Label className="text-sm font-medium">
            {/* BM-H3509: these four are fees, not disbursements (BM-H3424). */}
            Have you noted all disbursements, and any of the fees {INVOICE_REQUEST_FEES.map(f => `${f.label} (${feeRate(f)})`).join(", ")}, on the billing system? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.disbursements_noted}
            onValueChange={(value) => updateField("disbursements_noted", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="disb_noted_yes" data-testid="disbursements-noted-yes" />
              <Label htmlFor="disb_noted_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="disb_noted_no" data-testid="disbursements-noted-no" />
              <Label htmlFor="disb_noted_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <Label className="text-sm font-medium">
            Would you like Accounts to add any of these fees or disbursements? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.accounts_add_disbursements}
            onValueChange={(value) => updateField("accounts_add_disbursements", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="add_disb_yes" data-testid="accounts-add-disb-yes" />
              <Label htmlFor="add_disb_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="add_disb_no" data-testid="accounts-add-disb-no" />
              <Label htmlFor="add_disb_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Conditional: Show disbursement details if Yes */}
        {formData.accounts_add_disbursements === "Yes" && (
          <div className="space-y-4 p-4 bg-blue-50 rounded-md border border-blue-200">
            <h3 className="font-medium text-blue-900">Disbursement Details</h3>

            {INVOICE_REQUEST_FEES.map(fee => (
              <div key={fee.field}>
                <Label htmlFor={fee.field} className="text-sm font-medium">
                  {fee.label}
                </Label>
                <p className="text-xs text-gray-500 mb-2">How many are chargeable? ({feeRate(fee)})</p>
                <Select
                  value={formData[fee.field]}
                  onValueChange={(value) => updateField(fee.field, value)}
                >
                  <SelectTrigger className="w-full bg-white" data-testid={`${fee.field}-select`}>
                    <SelectValue placeholder="Select number" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEE_COUNT_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div>
              <Label htmlFor="other_disbursement" className="text-sm font-medium">
                Is there a disbursement that needs to be added?
              </Label>
              <Textarea
                id="other_disbursement"
                value={formData.other_disbursement}
                onChange={(e) => updateField("other_disbursement", e.target.value)}
                placeholder="Please provide a brief description, amount, and whether to add VAT"
                className="mt-1 min-h-[80px] bg-white"
                data-testid="other-disbursement-input"
              />
              <p className="text-xs text-gray-500 mt-1">Optional</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Step 4: Invoice Type & Basis
function Step4InvoiceType({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Invoice Type & Basis</h2>
        <p className="text-gray-600 text-sm">Specify the type and basis of this invoice</p>
      </div>

      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Label htmlFor="invoice_type" className="text-sm font-medium">
              Type of invoice <span className="text-status-red">*</span>
            </Label>
            <InfoTooltip id="invoice_type" size="sm" />
          </div>
          <Select
            value={formData.invoice_type}
            onValueChange={(value) => updateField("invoice_type", value)}
          >
            <SelectTrigger className="w-full mt-1" data-testid="invoice-type-select">
              <SelectValue placeholder="Select invoice type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Interim">Interim</SelectItem>
              <SelectItem value="Final">Final</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="wip_or_fixed" className="text-sm font-medium">
            Is this invoice WIP or fixed fee? <span className="text-status-red">*</span>
          </Label>
          <Select
            value={formData.wip_or_fixed}
            onValueChange={(value) => updateField("wip_or_fixed", value)}
          >
            <SelectTrigger className="w-full mt-1" data-testid="wip-fixed-select">
              <SelectValue placeholder="Select WIP or Fixed fee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="WIP">WIP</SelectItem>
              <SelectItem value="Fixed fee">Fixed fee</SelectItem>
              <SelectItem value="Disbursements only">Disbursements only</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Conditional: Show WIP check if WIP selected */}
        {formData.wip_or_fixed === "WIP" && (
          <div className="p-4 bg-status-amber-dim/30 rounded-md border border-amber-500/40">
            <Label className="text-sm font-medium text-status-amber">
              If WIP, have you checked all time recordings and ensured the language is appropriate for the invoice? <span className="text-status-red">*</span>
            </Label>
            <RadioGroup
              value={formData.wip_time_checked}
              onValueChange={(value) => updateField("wip_time_checked", value)}
              className="mt-3 space-y-2"
            >
              <div className="flex items-center space-x-3 p-3 rounded-md border border-amber-300 bg-white hover:bg-status-amber-dim/30">
                <RadioGroupItem value="Yes" id="wip_check_yes" data-testid="wip-time-checked-yes" />
                <Label htmlFor="wip_check_yes" className="cursor-pointer flex-1">Yes</Label>
              </div>
              <div className="flex items-center space-x-3 p-3 rounded-md border border-amber-300 bg-white hover:bg-status-amber-dim/30">
                <RadioGroupItem value="No" id="wip_check_no" data-testid="wip-time-checked-no" />
                <Label htmlFor="wip_check_no" className="cursor-pointer flex-1">No</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Brief 33 — Fixed-fee amount + narrative capture. Mirrors the
            WIP conditional block above; mandatory when "Fixed fee" is
            picked, hidden + state-ignored otherwise. Amount is a free-
            text string (Lawbillity is the source of truth for VAT /
            disbursements / calculation). Narrative IS the line the
            client will see on the Lawbillity invoice — 20-500 chars,
            free text. */}
        {formData.wip_or_fixed === "Fixed fee" && (
          <div className="p-4 bg-teal-dim/30 rounded-md border border-teal/40 space-y-4" data-testid="fixed-fee-block">
            <div>
              <Label htmlFor="fixed_fee_amount" className="text-sm font-medium text-teal">
                Fixed fee amount <span className="text-status-red">*</span>
              </Label>
              <p className="text-xs text-slate-500 mt-1 mb-2">
                Free text — Lawbillity handles VAT and any disbursements. Write what Accounts needs to see (e.g. "£1,250", "1250 + agreed disbursements", "£950 incl scheme 2 cap").
              </p>
              <Input
                id="fixed_fee_amount"
                data-testid="fixed-fee-amount"
                value={formData.fixed_fee_amount}
                onChange={(e) => updateField("fixed_fee_amount", e.target.value)}
                maxLength={100}
                placeholder="e.g. 1,250"
              />
            </div>
            <div>
              <Label htmlFor="fixed_fee_narrative" className="text-sm font-medium text-teal">
                Fixed fee narrative (this is what the client will see on the invoice) <span className="text-status-red">*</span>
              </Label>
              <p className="text-xs text-slate-500 mt-1 mb-2">
                20-500 characters. Write the exact wording you want on the invoice line.
              </p>
              {/* BM-H347 — standard narrative picker. Fixed-fee branch
                  only (it sits inside this block). Writes the chosen
                  wording into fixed_fee_narrative below as ordinary
                  editable text, plus the code for later measurement. */}
              <div className="mb-3">
                <FixedFeeNarrativePicker
                  transactionType={formData.matter_transaction_type}
                  selectedCode={formData.fixed_fee_narrative_template_code}
                  onSelect={(template) => {
                    updateField("fixed_fee_narrative", template.narrative);
                    updateField(
                      "fixed_fee_narrative_template_code",
                      template.code
                    );
                  }}
                />
              </div>
              <Textarea
                id="fixed_fee_narrative"
                data-testid="fixed-fee-narrative"
                value={formData.fixed_fee_narrative}
                onChange={(e) => updateField("fixed_fee_narrative", e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="e.g. Fixed fee for preparation and completion of purchase of [property] dated [date]"
              />
              <p
                className="text-xs text-slate-400 mt-1"
                data-testid="fixed-fee-narrative-counter"
              >
                {(formData.fixed_fee_narrative || "").length} / 500
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Step 5: MOA & Third Party Invoice
function Step5MOAThirdParty({ formData, updateField, thirdPartyFile, onFileChange, onSharePointFile }) {
  const [dragOver, setDragOver] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Simulate a file input change event
      onFileChange({ target: { files: [file] } });
    }
  };
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Money on Account & Third Party Invoice</h2>
        <p className="text-gray-600 text-sm">MOA and third party invoice details</p>
      </div>

      {/* MOA Section */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-900 flex items-center gap-2">
          <Banknote className="w-5 h-5 text-teal" />
          Money on Account
        </h3>

        <div>
          <Label className="text-sm font-medium">
            Do we hold money on account? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.hold_moa}
            onValueChange={(value) => updateField("hold_moa", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="hold_moa_yes" data-testid="hold-moa-yes" />
              <Label htmlFor="hold_moa_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="hold_moa_no" data-testid="hold-moa-no" />
              <Label htmlFor="hold_moa_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Conditional: MOA Yes follow-ups */}
        {formData.hold_moa === "Yes" && (
          <div className="space-y-4 p-4 bg-status-green-dim rounded-md border border-green-200">
            <div>
              <Label className="text-sm font-medium text-status-green">
                If yes, should it be used against this invoice? <span className="text-status-red">*</span>
              </Label>
              <p className="text-xs text-status-green mb-2">This will form a client to office transfer request to fully or partly settle this invoice</p>
              <RadioGroup
                value={formData.use_moa}
                onValueChange={(value) => updateField("use_moa", value)}
                className="mt-2 space-y-2"
              >
                <div className="flex items-center space-x-3 p-3 rounded-md border border-green-300 bg-white hover:bg-status-green-dim">
                  <RadioGroupItem value="Yes" id="use_moa_yes" data-testid="use-moa-yes" />
                  <Label htmlFor="use_moa_yes" className="cursor-pointer flex-1">Yes</Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-md border border-green-300 bg-white hover:bg-status-green-dim">
                  <RadioGroupItem value="No" id="use_moa_no" data-testid="use-moa-no" />
                  <Label htmlFor="use_moa_no" className="cursor-pointer flex-1">No</Label>
                </div>
              </RadioGroup>
            </div>

            {formData.use_moa === "Yes" && (
              <div>
                <Label className="text-sm font-medium text-status-green">
                  If you are requesting that MOA be used against this invoice, have you checked that none should be retained for an undertaking/disbursement? <span className="text-status-red">*</span>
                </Label>
                <RadioGroup
                  value={formData.moa_retention_checked}
                  onValueChange={(value) => updateField("moa_retention_checked", value)}
                  className="mt-2 space-y-2"
                >
                  <div className="flex items-center space-x-3 p-3 rounded-md border border-green-300 bg-white hover:bg-status-green-dim">
                    <RadioGroupItem value="Yes" id="moa_retention_yes" data-testid="moa-retention-yes" />
                    <Label htmlFor="moa_retention_yes" className="cursor-pointer flex-1">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-md border border-green-300 bg-white hover:bg-status-green-dim">
                    <RadioGroupItem value="No" id="moa_retention_no" data-testid="moa-retention-no" />
                    <Label htmlFor="moa_retention_no" className="cursor-pointer flex-1">No</Label>
                  </div>
                </RadioGroup>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Third Party Invoice Section */}
      <div className="space-y-4 pt-6 border-t border-gray-200">
        <h3 className="font-medium text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal" />
          Third Party Invoice
        </h3>

        <div>
          <Label className="text-sm font-medium">
            Do you need a third party invoice to be settled within this invoice? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.third_party_invoice}
            onValueChange={(value) => updateField("third_party_invoice", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="third_party_yes" data-testid="third-party-yes" />
              <Label htmlFor="third_party_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="third_party_no" data-testid="third-party-no" />
              <Label htmlFor="third_party_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>

        {/* Conditional: Third party invoice details */}
        {formData.third_party_invoice === "Yes" && (
          <div className="space-y-4 p-4 bg-purple-50 rounded-md border border-purple-200">
            <div>
              <Label className="text-sm font-medium text-purple-900">
                Please upload a copy of the third party invoice <span className="text-status-red">*</span>
              </Label>

              {/* Show selected file */}
              {(thirdPartyFile || formData.third_party_file_name) && (
                <div className="mt-2 flex items-center gap-2 p-3 bg-white border border-purple-300 rounded-md">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-purple-800 font-medium flex-1 truncate">
                    {thirdPartyFile?.name || formData.third_party_file_name}
                  </span>
                  {formData.third_party_sp_url && (
                    <span className="text-[10px] text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded">SharePoint</span>
                  )}
                </div>
              )}

              {/* Upload from device (supports drag-drop) */}
              <div
                className="mt-2"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label className={cn(
                  "flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-md cursor-pointer bg-white transition-colors",
                  dragOver ? "border-teal bg-teal-dim" : "border-purple-300 hover:bg-purple-50"
                )}>
                  <div className="flex flex-col items-center justify-center py-3">
                    <Upload className="w-6 h-6 mb-1 text-purple-500" />
                    <p className="text-sm text-gray-500">{thirdPartyFile ? "Replace file from device" : "Upload or drag file here"}</p>
                    <p className="text-xs text-gray-400">PDF, DOC, or image files</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    onChange={onFileChange}
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    data-testid="third-party-file-input"
                  />
                </label>
              </div>

              {/* Pick from SharePoint */}
              {formData.matter_reference && (
                <button
                  type="button"
                  onClick={onSharePointFile}
                  className="mt-2 w-full flex items-center justify-center gap-2 h-10 border-2 border-dashed border-blue-300 rounded-md bg-white hover:bg-blue-50 text-sm text-blue-600 transition-colors"
                  data-testid="third-party-sp-picker"
                >
                  <Folder className="w-4 h-4" />
                  Pick from SharePoint
                </button>
              )}

              {!thirdPartyFile && !formData.third_party_file_name && (
                <p className="text-xs text-gray-500 mt-1">No file uploaded yet</p>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium text-purple-900">
                Do you require Accounts to be notified of a CTO for settlement of the third party invoice? <span className="text-status-red">*</span>
              </Label>
              <RadioGroup
                value={formData.cto_notification}
                onValueChange={(value) => updateField("cto_notification", value)}
                className="mt-2 space-y-2"
              >
                <div className="flex items-center space-x-3 p-3 rounded-md border border-purple-300 bg-white hover:bg-purple-50">
                  <RadioGroupItem value="Yes" id="cto_yes" data-testid="cto-yes" />
                  <Label htmlFor="cto_yes" className="cursor-pointer flex-1">Yes</Label>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-md border border-purple-300 bg-white hover:bg-purple-50">
                  <RadioGroupItem value="No" id="cto_no" data-testid="cto-no" />
                  <Label htmlFor="cto_no" className="cursor-pointer flex-1">No</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Step 6: Delivery Preferences
function Step6Delivery({ formData, updateField }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Delivery Preferences</h2>
        <p className="text-gray-600 text-sm">How should this invoice be handled?</p>
      </div>

      <div className="space-y-6">
        <div>
          <Label className="text-sm font-medium">
            Do you require a draft before the invoice is sent to the client? <span className="text-status-red">*</span>
          </Label>
          <RadioGroup
            value={formData.draft_required}
            onValueChange={(value) => updateField("draft_required", value)}
            className="mt-3 space-y-2"
          >
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="Yes" id="draft_yes" data-testid="draft-required-yes" />
              <Label htmlFor="draft_yes" className="cursor-pointer flex-1">Yes</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50">
              <RadioGroupItem value="No" id="draft_no" data-testid="draft-required-no" />
              <Label htmlFor="draft_no" className="cursor-pointer flex-1">No</Label>
            </div>
          </RadioGroup>
        </div>

        <div>
          <Label htmlFor="notification_preference" className="text-sm font-medium">
            Do you wish to be notified when the invoice has been sent or copied in? <span className="text-status-red">*</span>
          </Label>
          <Select
            value={formData.notification_preference}
            onValueChange={(value) => updateField("notification_preference", value)}
          >
            <SelectTrigger className="w-full mt-1" data-testid="notification-pref-select">
              <SelectValue placeholder="Select preference" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Notify me when sent">Notify me when sent</SelectItem>
              <SelectItem value="Copy me into the email">Copy me into the email</SelectItem>
              <SelectItem value="No notification required">No notification required</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="further_info" className="text-sm font-medium">
            Please add any further information for Accounts
          </Label>
          <Textarea
            id="further_info"
            value={formData.further_info}
            onChange={(e) => updateField("further_info", e.target.value)}
            placeholder="Any additional notes or instructions for the Accounts team"
            className="mt-1 min-h-[100px]"
            data-testid="further-info-input"
          />
          <p className="text-xs text-gray-500 mt-1">Optional</p>
        </div>
      </div>
    </div>
  );
}

// Step 7: Review & Submit
function Step7Review({ formData, thirdPartyFile }) {
  const ReviewSection = ({ title, children }) => (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-teal uppercase tracking-wide mb-3">{title}</h3>
      <div className="bg-gray-50 rounded-md p-4 space-y-2">
        {children}
      </div>
    </div>
  );

  const ReviewField = ({ label, value, highlight = false }) => (
    <div className="flex justify-between py-1 gap-3">
      <span className="text-gray-600 text-sm shrink-0">{label}</span>
      <span className={cn("text-sm font-medium text-gray-900 text-right break-words", highlight && "text-gold")}>{value || "—"}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-teal mb-2">Review & Submit</h2>
        <p className="text-gray-600 text-sm">Please review all details before submitting</p>
      </div>

      {formData.is_test_submission && (
        <div className="p-3 bg-status-amber-dim/30 border border-amber-500/40 rounded-md flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-status-amber" />
          <span className="text-status-amber font-medium">This is a TEST submission</span>
        </div>
      )}

      <ReviewSection title="Invoice Information">
        <ReviewField label="Requester" value={formData.requester_name} />
        <ReviewField label="Email" value={formData.requester_email} />
        <ReviewField label="Date" value={formData.date} />
        <ReviewField label="Client Name" value={formData.client_name} />
        <ReviewField label="Matter Title" value={formData.matter_title} />
        <ReviewField label="Matter Reference" value={formData.matter_reference} highlight />
      </ReviewSection>

      <ReviewSection title="Client Contact">
        <ReviewField label="Invoice Email" value={formData.client_invoice_email} />
        <ReviewField label="Comments" value={formData.invoice_comments} />
      </ReviewSection>

      <ReviewSection title="Disbursements">
        {formData.lawbillity_expenses?.length > 0 && (
          <div className="mb-3">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">LawBillity Expenses ({formData.lawbillity_expenses.length})</span>
            <div className="mt-1 space-y-1">
              {formData.lawbillity_expenses.map((exp, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-gray-600">{exp.description || "Expense"}</span>
                  <span className="font-medium">£{(exp.amount || 0).toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm font-semibold pt-1 border-t border-gray-200">
                <span>LawBillity Total</span>
                <span>£{formData.lawbillity_expenses.reduce((s, e) => s + (e.amount || 0), 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
        <ReviewField label="All Disbursements Noted" value={formData.disbursements_noted} />
        <ReviewField label="Accounts to Add Disbursements" value={formData.accounts_add_disbursements} />
        {formData.accounts_add_disbursements === "Yes" && (
          <>
            {INVOICE_REQUEST_FEES.map(fee => (
              <ReviewField key={fee.field} label={fee.label} value={formData[fee.field] !== "0" ? `${formData[fee.field]} × ${feeRate(fee)}` : "None"} />
            ))}
            <ReviewField label="Other Disbursements" value={formData.other_disbursement} />
          </>
        )}
      </ReviewSection>

      <ReviewSection title="Invoice Type & Basis">
        <ReviewField label="Invoice Type" value={formData.invoice_type} highlight />
        <ReviewField label="WIP or Fixed Fee" value={formData.wip_or_fixed} />
        {formData.wip_or_fixed === "WIP" && (
          <ReviewField label="Time Recordings Checked" value={formData.wip_time_checked} />
        )}
        {/* Brief 33 — surface fixed-fee amount + narrative in review
            step so the fee earner sees exactly what Accounts will. */}
        {formData.wip_or_fixed === "Fixed fee" && (
          <>
            <ReviewField label="Fixed Fee Amount" value={formData.fixed_fee_amount} highlight />
            <ReviewField label="Fixed Fee Narrative" value={formData.fixed_fee_narrative} />
            {formData.fixed_fee_narrative_template_code && (
              <ReviewField
                label="Narrative Template"
                value={formData.fixed_fee_narrative_template_code}
              />
            )}
          </>
        )}
      </ReviewSection>

      <ReviewSection title="Money on Account">
        <ReviewField label="Hold MOA" value={formData.hold_moa} />
        {formData.hold_moa === "Yes" && (
          <>
            <ReviewField label="Use Against Invoice" value={formData.use_moa} highlight={formData.use_moa === "Yes"} />
            {formData.use_moa === "Yes" && (
              <ReviewField label="Retention Checked" value={formData.moa_retention_checked} />
            )}
          </>
        )}
      </ReviewSection>

      <ReviewSection title="Third Party Invoice">
        <ReviewField label="Third Party Invoice Required" value={formData.third_party_invoice} />
        {formData.third_party_invoice === "Yes" && (
          <>
            <ReviewField label="File" value={thirdPartyFile?.name || formData.third_party_file_name} />
            <ReviewField label="CTO Notification" value={formData.cto_notification} highlight={formData.cto_notification === "Yes"} />
          </>
        )}
      </ReviewSection>

      <ReviewSection title="Delivery Preferences">
        <ReviewField label="Draft Required" value={formData.draft_required} highlight={formData.draft_required === "Yes"} />
        <ReviewField label="Notification Preference" value={formData.notification_preference} />
        <ReviewField label="Further Information" value={formData.further_info} />
      </ReviewSection>

      {/* Summary badges */}
      {(() => {
        const badges = [];
        if (formData.accounts_add_disbursements === "Yes") badges.push("Disbursements to be added by Accounts");
        if (formData.third_party_invoice === "Yes") badges.push("Third party invoice required");
        if (formData.draft_required === "Yes") badges.push("Draft required before sending to client");
        if (formData.cto_notification === "Yes") badges.push("CTO notification required");
        if (formData.hold_moa === "Yes" && formData.use_moa === "Yes") badges.push("MOA to be applied to invoice");
        return badges.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {badges.map(b => (
              <span key={b} className="inline-block px-3 py-1.5 rounded-full text-xs font-medium bg-teal text-white">{b}</span>
            ))}
          </div>
        ) : null;
      })()}

      <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-blue-800 font-medium">Ready to submit?</p>
            <p className="text-blue-700 text-sm mt-1">
              Once submitted, this request will be sent to Accounts for processing.
              You will receive a confirmation email with a PDF summary.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
